<template>
  <div
    class="html-editor-content"
    :class="{ 'html-editor-content--block-links': blockExternalLinks }"
    v-html="contentForDisplay"
  />
</template>

<script>
import { applyDarkThemeColors, logUnmappedColors } from '../../utils/htmlDarkThemeColors';

const DARK_MODE_STORAGE_KEY = 'darkModeEnabled';

export default {
  name: 'HtmlEditorContent',
  emits: ['link-blocked'],
  props: {
    /** Conteúdo HTML a ser renderizado (v-html). */
    content: {
      type: String,
      default: '',
    },
    /** Tema de exibição: 'light' | 'dark'. Se não informado, usa o modo escuro do app (localStorage/body). */
    theme: {
      type: String,
      default: '',
      validator: (v) => !v || v === 'light' || v === 'dark',
    },
    /**
     * Quando true, impede navegação por links (a[href], area[href]) dentro do HTML
     * (clique, botão do meio, Enter/Espaço com foco no link). Emite link-blocked.
     */
    blockExternalLinks: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /** Tema efetivo, resolvido ao carregar o componente. */
      resolvedTheme: 'light',
    };
  },
  computed: {
    /** Conteúdo a exibir: com cores ajustadas para dark quando resolvedTheme === 'dark'. */
    contentForDisplay() {
      if (this.content) logUnmappedColors(this.content, '[HtmlEditorContent]');
      if (this.resolvedTheme !== 'dark' || !this.content) return this.content;
      return applyDarkThemeColors(this.content);
    },
  },
  mounted() {
    this.resolvedTheme = this.getResolvedTheme();
    this.bindLinkGuards();
  },
  beforeUnmount() {
    this.unbindLinkGuards();
  },
  methods: {
    bindLinkGuards() {
      const el = this.$el;
      if (!el || typeof el.addEventListener !== 'function') return;
      if (el.__htmlEditorLinkGuardsBound) return;
      el.__htmlEditorLinkGuardsBound = true;
      el.addEventListener('click', this.onContainerClickCapture, true);
      el.addEventListener('auxclick', this.onContainerAuxClickCapture, true);
      el.addEventListener('keydown', this.onContainerKeydownCapture, true);
    },
    unbindLinkGuards() {
      const el = this.$el;
      if (!el || typeof el.removeEventListener !== 'function') return;
      el.removeEventListener('click', this.onContainerClickCapture, true);
      el.removeEventListener('auxclick', this.onContainerAuxClickCapture, true);
      el.removeEventListener('keydown', this.onContainerKeydownCapture, true);
      el.__htmlEditorLinkGuardsBound = false;
    },
    findActivatableLink(target) {
      if (!target || typeof target.closest !== 'function') return null;
      return target.closest('a[href], area[href]');
    },
    isNavigableHref(hrefAttr) {
      if (hrefAttr === null || hrefAttr === undefined) return false;
      return String(hrefAttr).trim() !== '';
    },
    shouldInterceptLinkEvent(event) {
      if (!this.blockExternalLinks) return false;
      if (event.defaultPrevented) return false;
      const link = this.findActivatableLink(event.target);
      if (!link || !this.isNavigableHref(link.getAttribute('href'))) return false;
      return true;
    },
    onContainerClickCapture(event) {
      if (!this.shouldInterceptLinkEvent(event)) return;
      event.preventDefault();
      event.stopPropagation();
      this.$emit('link-blocked');
    },
    onContainerAuxClickCapture(event) {
      if (event.button !== 1) return;
      if (!this.shouldInterceptLinkEvent(event)) return;
      event.preventDefault();
      event.stopPropagation();
      this.$emit('link-blocked');
    },
    onContainerKeydownCapture(event) {
      if (!this.blockExternalLinks) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const link = this.findActivatableLink(event.target);
      if (!link || !this.isNavigableHref(link.getAttribute('href'))) return;
      event.preventDefault();
      event.stopPropagation();
      this.$emit('link-blocked');
    },
    getResolvedTheme() {
      if (this.theme === 'light' || this.theme === 'dark') return this.theme;
      const darkActive =
        (typeof localStorage !== 'undefined' && localStorage.getItem(DARK_MODE_STORAGE_KEY) === 'true') ||
        (typeof document !== 'undefined' && document.body.classList.contains('dark-mode'));
      return darkActive ? 'dark' : 'light';
    },
  },
};
</script>

<style scoped>
.html-editor-content--block-links :deep(a[href]),
.html-editor-content--block-links :deep(area[href]) {
  cursor: not-allowed;
}
</style>
